import React, { useEffect, useState } from "react";
import Sidebar from "../components/Nav/AdminSidebar";
import { FaSearch, FaTimes, FaFileExcel, FaTrash } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const paymentStatusOptions = ["", "Completed", "Pending", "Failed", "Refunded"];

export default function Transactions() {
  const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append("search", search.trim());
      if (paymentStatus) params.append("payment_status", paymentStatus);

      const res = await fetch(
        `${BASE_URL}/payment/get-transactions.php?${params.toString()}`,
        {
          credentials: "include",
        }
      );
      const json = await res.json();
      if (json.success) {
        setTransactions(json.data);
      } else {
        alert("Failed to fetch data: " + json.message);
      }
    } catch (err) {
      alert("Error fetching transactions: " + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 500);
    return () => clearTimeout(timer);
  }, [search, paymentStatus]);

  const clearFilters = () => {
    setSearch("");
    setPaymentStatus("");
  };

  const exportToExcel = () => {
    if (!transactions.length) {
      alert("No transactions to export");
      return;
    }
    // Map transactions to flat data suitable for Excel
    const data = transactions.map((tx) => ({
      "Transaction ID": tx.transaction_id,
      "Booking ID": tx.booking_id,
      "Customer Name": tx.customer_name,
      "Customer Email": tx.customer_email,
      "Room No": tx.room_no,
      "Check In": new Date(tx.check_in_time).toLocaleDateString("en-GB"),
      "Check Out": new Date(tx.check_out_time).toLocaleDateString("en-GB"),
      Amount: `${tx.currency} ${parseFloat(tx.amount).toFixed(2)}`,
      Status: tx.payment_status,

      "Booked Date": new Date(tx.payment_date).toLocaleDateString("en-GB"),
      Staff: tx.staff_name || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "roombooking_transactions.xlsx");
  };

  const deleteTransaction = async (transactionId) => {
    if (!window.confirm("Are you sure you want to delete this transaction?"))
      return;
    setDeletingId(transactionId);
    try {
      const res = await fetch(`${BASE_URL}/payment/deleteTransaction.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transaction_id: transactionId }),
      });
      const json = await res.json();
      if (json.success) {
        alert("Transaction deleted successfully");
        fetchTransactions();
      } else {
        alert("Failed to delete transaction: " + json.message);
      }
    } catch (err) {
      alert("Error deleting transaction: " + err.message);
    }
    setDeletingId(null);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 px-6 py-6 max-w-full">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">
          🧾 Transactions Overview
        </h1>

        {/* Filters and actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 mb-6 max-w-full">
          {/* Search */}
          <div className="relative w-full max-w-md flex-grow mb-3 sm:mb-0">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search by customer, email, booking or transaction ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-10 py-2 rounded-xl border border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Payment Status Filter */}
          <div className="w-full max-w-xs">
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full py-2 px-4 rounded-xl border border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
            >
              {paymentStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status === "" ? "All Payment Statuses" : status}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <button
            onClick={clearFilters}
            className="mt-3 sm:mt-0 sm:ml-auto px-4 py-2 bg-red-500 text-white rounded-xl shadow hover:bg-red-600 transition"
            aria-label="Clear all filters"
          >
            Clear Filters
          </button>

          {/* Export Excel Button */}
          <button
            onClick={exportToExcel}
            className="mt-3 sm:mt-0 ml-2 px-4 py-2 bg-green-600 text-white rounded-xl shadow hover:bg-green-700 flex items-center gap-2 transition"
            aria-label="Export transactions as Excel"
          >
            <FaFileExcel /> Export Excel
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-200 bg-white">
          <table className="min-w-full text-sm text-left border-collapse">
            <thead className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 font-semibold tracking-wide whitespace-nowrap">
                  Booking ID
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Customer
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Email
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Room
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Check In
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Check Out
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Amount
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Status
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Booked Date
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Staff
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center p-6 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center p-6 text-gray-500">
                    No transactions found.
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr
                    key={tx.booking_id}
                    className="hover:bg-gray-50 transition-all duration-200"
                  >
                    <td className="px-4 py-3 font-mono text-indigo-600 whitespace-nowrap">
                      {tx.booking_id}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                      {tx.customer_name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {tx.customer_email}
                    </td>
                    <td className="px-4 py-3 font-mono whitespace-nowrap">
                      {tx.room_no}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(tx.check_in_time).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(tx.check_out_time).toLocaleDateString("en-GB")}
                    </td>

                    <td className="px-4 py-3 font-semibold text-blue-700 whitespace-nowrap">
                      {tx.currency} {parseFloat(tx.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium tracking-wide whitespace-nowrap ${
                          tx.payment_status === "Completed"
                            ? "bg-green-100 text-green-800"
                            : tx.payment_status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : tx.payment_status === "Failed"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {tx.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-500 whitespace-nowrap">
                      {new Date(tx.payment_date).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-500 whitespace-nowrap">
                      {tx.staff_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <button
                        disabled={deletingId === tx.transaction_id}
                        onClick={() => deleteTransaction(tx.transaction_id)}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Delete Transaction"
                        aria-label={`Delete transaction ${tx.transaction_id}`}
                      >
                        <FaTrash size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
